import React from "react";
import { INPUT } from "@/components/pageSettings/AdminModal";
import {
  AT_FAULT_INS_OPTIONS, ATTORNEY_OPTIONS, CASE_TYPES, FAULT_LEVELS, HITRUN_OPTIONS, INCIDENT_AGE_PRESETS,
  LEAD_TYPES, TRISTATE_OPTIONS,
} from "@/lib/buyerCriteriaOptions";
import { Field } from "./StepShell";
import { AcceptHours } from "./AcceptHours";

const ASKED = [
  ["Physical injury", "injury_required", TRISTATE_OPTIONS], ["Medical treatment", "treatment_required", TRISTATE_OPTIONS],
  ["Has an attorney", "attorney_policy", ATTORNEY_OPTIONS], ["Other driver insured", "at_fault_party_insured", AT_FAULT_INS_OPTIONS],
];
const NOT_ASKED = [
  ["Police report filed", "police_report_required", TRISTATE_OPTIONS], ["Hit-and-run", "hit_and_run", HITRUN_OPTIONS],
  ["Claimant insured", "claimant_insured", TRISTATE_OPTIONS], ["Uninsured motorist", "uninsured_motorist", HITRUN_OPTIONS],
];
const AGES = INCIDENT_AGE_PRESETS.filter((p) => p.value !== "custom");

function Chips({ label, hint, options, value, onChange, testId }) {
  const toggle = (code) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code].sort());
  return (
    <div data-testid={testId}>
      <div className="label-mono">{label}</div>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {options.map((o) => {
          const on = value.includes(o.code);
          return (
            <button key={o.code} type="button" onClick={() => toggle(o.code)} aria-pressed={on} data-testid={`${testId}-${o.code}`}
                    className={`border-2 border-black px-2 py-1 text-[11px] transition-colors duration-100 ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}

function Rule({ label, k, options, crit, set }) {
  return (
    <Field label={label}>
      <select className={INPUT} value={crit[k] || "no_requirement"} data-testid={`setup-rule-${k}`}
              onChange={(e) => set(k, e.target.value === "no_requirement" ? null : e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

function AgeRule({ crit, set }) {
  const v = crit.max_incident_age_days;
  const custom = v != null && !AGES.some((p) => p.value === v);
  return (
    <Field label="Accident happened within">
      <select className={INPUT} value={v == null ? "" : String(v)} data-testid="setup-rule-incident-age"
              onChange={(e) => set("max_incident_age_days", e.target.value === "" ? null : Number(e.target.value))}>
        {AGES.map((p) => <option key={String(p.value)} value={p.value ?? ""}>{p.label}</option>)}
        {custom && <option value={String(v)}>{v} days</option>}
      </select>
    </Field>
  );
}

export function CriteriaRules({ crit, set }) {
  const rules = (list) => list.map(([label, k, options]) => <Rule key={k} label={label} k={k} options={options} crit={crit} set={set} />);
  return (
    <div className="space-y-6">
      <Chips label="Case types" hint="None picked = every case type." options={CASE_TYPES} value={crit.case_types || []}
             onChange={(v) => set("case_types", v)} testId="setup-case-types" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AgeRule crit={crit} set={set} />
        {rules(ASKED)}
      </div>
      <Chips label="Fault levels they accept" hint="None picked = any fault level." options={FAULT_LEVELS}
             value={crit.accepted_fault_levels || []} onChange={(v) => set("accepted_fault_levels", v)} testId="setup-fault-levels" />
      <div data-testid="setup-rule-hours">
        <div className="label-mono mb-1">Lead hours</div>
        <AcceptHours value={crit.accept_schedule} onChange={(v) => set("accept_schedule", v)} />
      </div>
      <details className="border-2 border-black p-3" data-testid="setup-more-rules">
        <summary className="cursor-pointer label-mono min-h-[32px] flex items-center">More rules (the web pages don't ask these)</summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          {rules(NOT_ASKED)}
        </div>
        <div className="mt-4">
          <Chips label="Lead types" hint="None picked = every lead type." options={LEAD_TYPES} value={crit.accepted_lead_types || []}
                 onChange={(v) => set("accepted_lead_types", v)} testId="setup-lead-types" />
        </div>
      </details>
      <Field label="Notes for your team (never used to reject leads)">
        <textarea rows={3} className={INPUT} value={crit.special_instructions || ""}
                  onChange={(e) => set("special_instructions", e.target.value)} data-testid="setup-rule-notes" />
      </Field>
    </div>
  );
}
